'use client';

import { useField } from 'formik';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { STATE_CODES } from '@commons/i18n/en/locations';

import Select, { type Option, type SelectProps } from './';

// Type definition for the StateSelect component properties.

type Props = Omit<SelectProps, 'options'> & {
  useLabelAsValue?: boolean; // New prop to control value type
  variant?: 'primary' | 'secondary';
};

/**
 * StateSelect component.
 *
 * This component renders a dropdown to select states from the United States.
 */

export const StateSelect = ({ useLabelAsValue = false, variant = 'primary', ...props }: Props) => {
  const t = useTranslations('locations.states');

  const options: Option[] = useMemo(() => {
    return STATE_CODES.map((code) => ({
      label: t(code),
      value: useLabelAsValue ? t(code) : code,
    }));
  }, [t, useLabelAsValue]);

  return <Select {...props} variant={variant} options={options} searchable />;
};

/**
 * FormikStateSelect component.
 *
 * This is a Formik-compatible wrapper for `StateSelect`, managing form state and error feedback using Formik's context.
 */

export const FormikStateSelect = ({ name, ...props }: Props) => {
  const [field, meta, helpers] = useField(name);

  const { touched, error } = meta;
  const { setValue } = helpers;

  const isError = touched && error !== undefined;

  return (
    <div className="notranslate" translate="no">
      <StateSelect onValueChange={setValue} error={isError} {...field} {...props} />
    </div>
  );
};

export default StateSelect;
